import type { Presale as GQLItem } from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * A presale for an event — one row of the ticketing modal's Presales tab.
 *
 * Three fields are **read-only**: `link`, `url` and `active`. None of them is on
 * `PresaleAttributes`, so the server never accepts them back, and `PresaleInput.toGqlInput`'s
 * whitelist is what strips them off a record a form was seeded from. They are marked in JSDoc
 * rather than with a TypeScript `readonly` modifier — no domain type under `App/` uses one, and a
 * `readonly` field still serialises into a mutation variable, so it would document the constraint
 * without enforcing it.
 *
 * `createdAt` and `updatedAt` are on the wire type and deliberately unmapped: nothing the presale
 * designs render reads them. Same call `WidgetConfig.fromGql` makes on `token` — add the field to
 * the type, the fragment and the `take` list below together if a screen ever needs one.
 *
 * Two notes that apply to every mapper in `App/`, repeated here because both bite on this type:
 *
 * - **Nullable scalars arrive as `null`, not `undefined`.** `take` (`util/takePayload.ts`) skips
 *   only `undefined`, so the `?:` types below under-describe what the mapper produces. The
 *   contract is that the two mean the same absence; widen the type rather than have the mapper
 *   guess if a caller ever needs to tell them apart.
 * - **`take` does not typecheck its key list**, so a misspelled or forgotten key yields
 *   `undefined` with no compile error. Hence the whole-object assertion opening `Presale.spec.ts`.
 */
export type Presale = {
  id: number;
  eventId: number;
  name: string;
  password?: string;
  sellStart?: string;
  sellEnd?: string;
  showUpcomingInfo?: boolean;
  ticketBlueprintIds: number[];
  /** Read-only: the URL-safe slug the public presale route is keyed on. */
  link?: string;
  /** Read-only: the full shareable URL, resolved per event server-side. */
  url?: string;
  /** Read-only: derived server-side from the sell window. */
  active: boolean;
};

export const fromGql = (item: GQLItem): Presale =>
  take<Presale>(item, 'id', 'eventId', 'name', 'password', 'sellStart', 'sellEnd', 'showUpcomingInfo', 'ticketBlueprintIds', 'link', 'url', 'active');
